import React, { useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';

/**
 * A horizontally scrolling strip of tappable period boxes — days on the Daily round, months on
 * Bills and Dues.
 *
 * Replaces bare ‹ › chevrons, which are a learned convention: they carry no meaning on their own
 * and nothing on screen says what they change. A named, numbered box needs nothing learned, and
 * the strip also shows where you currently are.
 *
 * Boxes are deliberately all one size. Enlarging the selected one reflows every box after it on
 * each tap, while the strip is animating to re-centre — the row shifts under the finger.
 *
 * @param items      [{ key, top, bottom, accent?, disabled? }] — `accent` outlines "today"/"current"
 * @param selectedKey  key of the chosen item
 * @param onSelect     (key, item) => void
 * @param boxWidth     px, default 44
 * @param endAdornment optional node pinned to the right (e.g. a calendar picker)
 */
export default function ScrollStrip({
    items = [], selectedKey, onSelect, boxWidth = 44, endAdornment = null,
}) {
    const ref = useRef(null);

    // Keep the chosen box on screen. Without this, picking a far period from a calendar leaves
    // the strip parked wherever it was, with nothing highlighted in view.
    useEffect(() => {
        const el = ref.current?.querySelector(`[data-key="${selectedKey}"]`);
        if (el) el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }, [selectedKey, items.length]);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box ref={ref} sx={{
                display: 'flex', gap: 0.5, overflowX: 'auto', flex: 1, py: 0.25,
                '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none',
            }}>
                {items.map((it) => {
                    const selected = it.key === selectedKey;
                    return (
                        <Box key={it.key} data-key={it.key}
                            onClick={() => !it.disabled && onSelect?.(it.key, it)}
                            sx={{
                                flexShrink: 0, width: boxWidth, py: 0.4, borderRadius: 1.5,
                                textAlign: 'center', userSelect: 'none',
                                cursor: it.disabled ? 'default' : 'pointer',
                                opacity: it.disabled ? 0.4 : 1,
                                border: '1.5px solid',
                                borderColor: selected ? 'primary.main'
                                    : it.accent ? 'primary.light' : 'divider',
                                bgcolor: selected ? 'primary.main' : 'background.paper',
                                color: selected ? 'common.white' : 'text.primary',
                                '&:active': { bgcolor: selected ? 'primary.dark' : 'action.selected' },
                            }}>
                            <Typography variant="caption" noWrap sx={{
                                display: 'block', lineHeight: 1.1, fontSize: '0.62rem',
                                color: selected ? 'inherit' : 'text.secondary',
                            }}>
                                {it.top}
                            </Typography>
                            <Typography variant="body2" fontWeight={700} noWrap sx={{ lineHeight: 1.25 }}>
                                {it.bottom}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
            {endAdornment}
        </Box>
    );
}
